'use client';

import React, { useState } from 'react';
import { NewsItem, CalendarEvent, Discipline } from '@/lib/types';
import FilterBar from './FilterBar';
import NewsVisual from './NewsVisual';
import { Calendar, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { calendarEventMatchesDiscipline, calendarEventsForSurface } from '@/lib/calendar';

interface NewsSectionProps {
  news: NewsItem[];
  calendar: CalendarEvent[];
  disciplines: Discipline[];
}

export default function NewsSection({ news, calendar, disciplines }: NewsSectionProps) {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredNews = news.filter(
    (item) => activeFilter === 'all' || item.discipline === activeFilter,
  );

  const filteredCalendar = calendarEventsForSurface(calendar, 'agenda').filter((event) =>
    calendarEventMatchesDiscipline(event, activeFilter),
  );

  return (
    <section className="bg-white py-24">
      <div className="container mx-auto px-4">
        <FilterBar
          disciplines={disciplines}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />

        <div className="grid grid-cols-1 justify-center gap-8 lg:grid-cols-[auto_400px]">
          {/* News Column */}
          <div>
            <div
              className={cn(
                'mb-12 flex flex-wrap items-center gap-4',
                filteredNews.length === 0 && 'flex-col items-start',
              )}
            >
              <h2 className="text-4xl font-black tracking-tight uppercase">Actualités</h2>
              {filteredNews.length > 0 && (
                <Link
                  href="/actualites"
                  className="text-red hover:bg-red hover:border-red inline-flex items-center gap-1 rounded-full border border-red-100 bg-red-50 px-4 py-2 text-sm font-bold transition-all hover:text-white"
                >
                  Voir tout <ChevronRight size={18} />
                </Link>
              )}
            </div>

            <div className="space-y-5">
              {filteredNews.length > 0 ? (
                filteredNews.map((item, index) => (
                  <Link
                    key={item.id}
                    href={item.url}
                    className="group hover:border-red/30 flex gap-5 rounded-2xl border border-gray-100 bg-white p-6 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="relative h-32 w-36 flex-shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-gray-200 to-gray-300">
                      <NewsVisual
                        image={item.image}
                        alt={item.title}
                        sizes="144px"
                        loading="eager"
                        imageClassName="object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      {index === 0 && (
                        <div className="bg-red absolute top-3 right-3 rounded-full px-3 py-1 text-xs font-bold text-white">
                          À la une
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col justify-between">
                      <div>
                        <span className="text-red bg-red/10 mb-2 inline-block rounded-full px-2.5 py-1 text-xs font-bold tracking-wider uppercase">
                          {item.dateDisplay}
                        </span>
                        <h3 className="text-navy group-hover:text-red mb-3 line-clamp-2 text-lg font-black transition-colors">
                          {item.title}
                        </h3>
                        <p className="line-clamp-2 text-sm text-gray-600">{item.excerpt}</p>
                      </div>
                      <div className="text-red flex items-center gap-2 text-xs font-bold tracking-wide uppercase transition-transform group-hover:translate-x-1">
                        Lire plus <ChevronRight size={14} />
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 py-20 text-center">
                  <p className="mb-6 font-medium text-gray-500">
                    Aucune actualité ne correspond à ce filtre.
                  </p>
                  <Link
                    href="/actualites"
                    className="bg-red hover:bg-red/90 inline-flex items-center gap-2 rounded-xl px-8 py-3 text-sm font-bold tracking-widest text-white uppercase transition-all"
                  >
                    Voir tout <ChevronRight size={18} />
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Calendar Column */}
          <div className="rounded-3xl bg-gray-50 p-8">
            <div className="mb-8 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-2xl font-black tracking-tight uppercase">
                <Calendar className="text-red" /> Agenda
              </h2>
            </div>

            <div className="space-y-8">
              {(() => {
                const currentYear = new Date().getFullYear();
                const grouped = filteredCalendar.reduce(
                  (acc, event) => {
                    const year = new Date(event.date).getFullYear();
                    if (!acc[year]) acc[year] = [];
                    acc[year].push(event);
                    return acc;
                  },
                  {} as Record<number, CalendarEvent[]>,
                );

                const sortedYears = Object.keys(grouped)
                  .map(Number)
                  .sort((a, b) => {
                    if (a === currentYear) return -1;
                    if (b === currentYear) return 1;
                    if (a > currentYear && b < currentYear) return -1;
                    if (b > currentYear && a < currentYear) return 1;
                    return a > currentYear ? a - b : b - a;
                  });

                return sortedYears.map((year) => (
                  <div key={year} className="space-y-4">
                    <h3 className="border-red border-l-2 pl-3 text-sm font-black tracking-widest text-gray-400 uppercase">
                      {year > currentYear ? 'À venir' : year === currentYear ? 'Cette année' : year}
                    </h3>
                    <div className="space-y-6">
                      {grouped[year]
                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                        .map((event) => (
                          <Link key={event.id} href={event.url} className="group flex gap-4">
                            <div className="group-hover:border-red flex h-16 w-14 flex-shrink-0 flex-col items-center justify-center rounded-lg border border-gray-100 bg-white shadow-sm transition-colors">
                              <span className="text-navy text-lg leading-none font-black">
                                {event.day}
                              </span>
                              <span className="text-red text-[10px] font-bold uppercase">
                                {event.month}
                              </span>
                            </div>
                            <div className="flex flex-col justify-center">
                              <h4 className="text-navy group-hover:text-red leading-tight font-bold transition-colors">
                                {event.title}
                              </h4>
                              <span className="mt-1 text-xs font-medium text-gray-400 uppercase">
                                {event.type}
                              </span>
                            </div>
                          </Link>
                        ))}
                    </div>
                  </div>
                ));
              })()}
            </div>

            <Link
              href="/agenda"
              className="border-navy/10 hover:bg-navy mt-10 flex w-full items-center justify-center rounded-xl border-2 py-4 text-sm font-bold tracking-widest uppercase transition-all hover:text-white"
            >
              Consulter l&apos;agenda complet
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
